// frontend/src/components/ContentCalendarBuilder.js
//
// Builds a content calendar CSV in the browser, on /tools/content-calendar-template.
//
// MEASURED 2026-08-16: `content calendar template` is 2,900/mo at KD 9 with CPC
// $14.38 — and it is one of only two targets in the whole size/template cluster
// with NO AI Overview. A template is a file, not a fact, so Google cannot answer
// it in a box. That is why it ranks second overall despite modest volume.
//
// THE DIFFERENTIATOR IS THE COLUMN HEADERS. The SERP is Canva, Notion, HubSpot
// and a roundup — every one of them a planning grid you fill in and then RETYPE
// into whatever actually publishes your posts. This generator emits the exact
// column set that Greatly Social's bulk importer already accepts
// (see /assets/greatly-social-bulk-template.csv), so the finished calendar
// uploads straight in. Nobody else on that SERP can do that, because nobody else
// on that SERP is a scheduler.
//
// Entirely client-side: dates computed here, CSV assembled here, downloaded via
// a blob. No endpoint, no cost, nothing stored.

import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Row, Col, Form, Button, Table } from 'react-bootstrap'
import { Download, CalendarWeek, ShieldCheck } from 'react-bootstrap-icons'

// Must match the bulk importer exactly — this is the whole point of the tool.
const COLUMNS = [
  'date', 'time', 'timezone', 'caption', 'media', 'networks',
  'caption_instagram', 'caption_linkedin', 'caption_x',
  'first_comment', 'alt_text', 'pinterest_board', 'youtube_title',
]

const NETWORKS = [
  ['instagram', 'Instagram'], ['facebook', 'Facebook'], ['tiktok', 'TikTok'],
  ['linkedin', 'LinkedIn'], ['x', 'X'], ['pinterest', 'Pinterest'],
  ['threads', 'Threads'], ['youtube', 'YouTube'],
]

const DAYS = [['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat'], ['0', 'Sun']]

// Content pillars, rotated through the empty caption column as prompts. These are
// planning scaffolding, not written captions — the point is that a blank calendar
// is the reason most people abandon one.
const PILLARS = [
  'Teach — something your customer gets wrong',
  'Show — behind the scenes of the work',
  'Proof — a result, review or before/after',
  'Offer — what you sell and who it is for',
  'Story — why you do this',
  'Ask — a question you actually want answered',
]

const pad = n => String(n).padStart(2, '0')
const guessTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
}

/** Wrap a CSV field only when it needs it, and escape embedded quotes. */
const cell = v => {
  const s = String(v ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export default function ContentCalendarBuilder () {
  const now = new Date()
  const [month, setMonth]   = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`)
  const [days, setDays]     = useState(['1', '3', '5'])
  const [time, setTime]     = useState('09:30')
  const [nets, setNets]     = useState(['instagram', 'facebook'])
  const [tz, setTz]         = useState(guessTz())
  const [prompts, setPrompts] = useState(true)
  const urlRef = useRef(null)

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current) }, [])

  const rows = useMemo(() => {
    const [y, m] = month.split('-').map(Number)
    if (!y || !m) return []
    const out = []
    const last = new Date(y, m, 0).getDate()
    let i = 0
    for (let d = 1; d <= last; d++) {
      const date = new Date(y, m - 1, d)
      if (!days.includes(String(date.getDay()))) continue
      out.push({
        date: `${y}-${pad(m)}-${pad(d)}`,
        time,
        timezone: tz,
        caption: prompts ? PILLARS[i % PILLARS.length] : '',
        networks: nets.join(','),
      })
      i++
    }
    return out
  }, [month, days, time, nets, tz, prompts])

  const csv = useMemo(() => {
    const lines = [COLUMNS.join(',')]
    for (const r of rows) {
      lines.push(COLUMNS.map(c => cell(r[c] ?? '')).join(','))
    }
    return lines.join('\n')
  }, [rows])

  const download = () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    urlRef.current = url
    const a = document.createElement('a')
    a.href = url
    a.download = `content-calendar-${month}.csv`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }

  const toggle = (list, setter, v) =>
    setter(list.includes(v) ? list.filter(x => x !== v) : [...list, v])

  return (
    <div className='p-4 p-md-5 rounded-3 shadow-card' style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <h2 className='fw-bold h4 mb-2'>
        <CalendarWeek className='text-primary me-2' />
        Build your content calendar
      </h2>
      <p className='text-muted mb-4'>
        Set your posting days and get a filled-in calendar for the month. It downloads as a CSV
        that <strong>uploads straight into Greatly Social</strong> — no retyping into a scheduler
        afterwards.
      </p>

      <Row className='g-3 mb-3'>
        <Col md={3}>
          <Form.Label className='fw-semibold small'>Month</Form.Label>
          <Form.Control type='month' value={month} onChange={e => setMonth(e.target.value)} />
        </Col>
        <Col md={3}>
          <Form.Label className='fw-semibold small'>Time</Form.Label>
          <Form.Control type='time' value={time} onChange={e => setTime(e.target.value)} />
        </Col>
        <Col md={6}>
          <Form.Label className='fw-semibold small'>Timezone</Form.Label>
          <Form.Control value={tz} onChange={e => setTz(e.target.value)} placeholder='e.g. Europe/London' />
        </Col>
      </Row>

      <Form.Label className='fw-semibold small'>Posting days</Form.Label>
      <div className='d-flex flex-wrap gap-2 mb-3'>
        {DAYS.map(([v, l]) => (
          <Button key={v} size='sm' variant={days.includes(v) ? 'primary' : 'outline-secondary'}
                  onClick={() => toggle(days, setDays, v)}>{l}</Button>
        ))}
      </div>

      <Form.Label className='fw-semibold small'>Networks</Form.Label>
      <div className='d-flex flex-wrap gap-2 mb-3'>
        {NETWORKS.map(([v, l]) => (
          <Button key={v} size='sm' variant={nets.includes(v) ? 'primary' : 'outline-secondary'}
                  onClick={() => toggle(nets, setNets, v)}>{l}</Button>
        ))}
      </div>

      <Form.Check
        type='switch' id='cal-prompts' className='mb-4'
        checked={prompts} onChange={e => setPrompts(e.target.checked)}
        label='Pre-fill each slot with a content prompt (a blank calendar is why most get abandoned)'
      />

      <div className='p-3 rounded-3 mb-3' style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
        <div className='d-flex align-items-center justify-content-between mb-2'>
          <span className='fw-semibold small'>
            {rows.length} post{rows.length === 1 ? '' : 's'} scheduled
            {nets.length ? ` to ${nets.length} network${nets.length === 1 ? '' : 's'}` : ''}
          </span>
          <Button variant='primary' size='sm' onClick={download} disabled={!rows.length || !nets.length}>
            <Download className='me-1' />Download CSV
          </Button>
        </div>
        {!nets.length && <div className='text-danger small'>Pick at least one network.</div>}
        {!days.length && <div className='text-danger small'>Pick at least one posting day.</div>}
        <div className='table-responsive' style={{ maxHeight: 260 }}>
          <Table size='sm' className='mb-0 bg-white'>
            <thead className='table-light'>
              <tr><th>date</th><th>time</th><th>networks</th><th>caption</th></tr>
            </thead>
            <tbody>
              {rows.slice(0, 8).map(r => (
                <tr key={r.date}>
                  <td style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.date}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{r.time}</td>
                  <td className='small text-muted'>{r.networks}</td>
                  <td className='small text-muted'>{r.caption || <em>blank</em>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {rows.length > 8 && (
            <div className='text-muted small p-2'>…and {rows.length - 8} more in the download</div>
          )}
        </div>
      </div>

      <p className='text-muted small'>
        <ShieldCheck className='me-1 text-success' />
        Built in your browser — nothing is uploaded and nothing is stored.
      </p>

      <div className='p-4 rounded-3 mt-4' style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', color: '#fff' }}>
        <h3 className='fw-bold h5 mb-2'>This file uploads straight into Greatly Social</h3>
        <p className='mb-3' style={{ color: 'rgba(255,255,255,.88)' }}>
          Fill in the captions, then upload it and the whole month is scheduled. Every other
          calendar template stops at the spreadsheet and leaves you to retype it.
        </p>
        <Button variant='light' href='/register' className='fw-semibold me-2 mb-2'>
          Start using Greatly Social free
        </Button>
        <Button variant='outline-light' href='/bulk-posting' className='mb-2'>
          See bulk upload
        </Button>
      </div>
    </div>
  )
}
